'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { useHandleQuickLink, type UrlTriggerEvent } from '../../hooks/UseNavigation';
import { containsPattern } from '../../utils/ContainsPattern';
// TODO: Import Icon when icons are added to the library
// import { Icon } from '@/components/ui/Icon';

import styles from './NavQuickLinkItem.module.scss';

/**
 * =============================================================================
 * ICON MAPPING - STUB
 * =============================================================================
 *
 * Maps icon identifiers from Contentful to actual icon components.
 *
 * TODO: Add these icons to the Icon component library:
 * - shipping (FreeShippingIcon)
 * - gift (GiftOfferedIcon)
 * - free-return (FreeReturnIcon)
 * - easy-contact (EasyContactIcon / concierge)
 *
 * Original icons from: src/assets/icons/
 * - concierge.svg
 * - gift.svg
 * - return.svg
 * - shipping.svg
 */
const ICON_MAPPING: Record<string, string> = {
  shipping: 'shipping',
  gift: 'gift',
  'free-return': 'return',
  'easy-contact': 'concierge',
};

/**
 * Media asset from Contentful
 */
export interface NavQuickLinkMedia {
  url?: string | null;
  title?: string | null;
  contentType?: string | null;
}

export interface NavQuickLinkItemProps {
  /** System ID */
  sys?: { id: string };
  /** Background color for image container */
  backgroundColor?: string | null;
  /** Icon identifier (maps to ICON_MAPPING) */
  icon?: string | null;
  /** Media asset (image) */
  media?: NavQuickLinkMedia | null;
  /** Display type from parent NavQuickLinks */
  type?: string | null;
  /** Navigation URL */
  url?: string | null;
  /** URL target (_self, _blank) */
  urlTarget?: string | null;
  /** Display text */
  urlText?: string | null;
  /** URL trigger event (opens modal instead of navigating) */
  urlTrigger?: UrlTriggerEvent | null;
  /** Optional className */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

/**
 * NavQuickLinkItem - Generic quick link item
 *
 * Renders as either:
 * - Text type: Link button with optional icon
 * - Default (Bubble): Circular icon/image with label
 *
 * Features:
 * - Icon mapping for predefined icons
 * - Media/image support
 * - Active state detection based on current URL
 * - URL trigger support for modal actions
 */
export function NavQuickLinkItem({
  backgroundColor,
  className,
  icon,
  media,
  type,
  url,
  urlTarget,
  urlText,
  urlTrigger,
  'data-testid': testId,
}: NavQuickLinkItemProps) {
  const pathname = usePathname();
  const { handleQuickLink } = useHandleQuickLink(url ?? '/', urlTarget ?? '_self', urlTrigger);

  // Text type variant (LinkButton style)
  if (type === 'Text') {
    const isContains = containsPattern(pathname, url);
    const isSelected = isContains || url === pathname;
    const isSvgImage = media?.contentType?.includes('svg') ?? false;

    return (
      <button
        className={clsx(
          styles.linkButton,
          isSelected && styles.selected,
          isSvgImage && styles.svgImage,
          className
        )}
        onClick={handleQuickLink}
        data-testid={testId}
        type="button"
      >
        <span className={styles.linkButtonWrapper}>
          {media?.url && (
            <span className={styles.linkButtonIcon}>
              <Image
                src={media.url}
                alt={media.title ?? ''}
                width={24}
                height={24}
                aria-hidden
                data-testid="servicesImage"
              />
            </span>
          )}
          <span className={styles.linkButtonText}>{urlText}</span>
        </span>
      </button>
    );
  }

  // Default type (Bubble) - circular icon/image with label
  const hasMatchingIcon = icon && icon in ICON_MAPPING;

  return (
    <button
      className={clsx(styles.wrapper, className)}
      onClick={handleQuickLink}
      data-testid={testId ?? 'NavQuickLinkItem'}
      type="button"
    >
      {/* Predefined icon */}
      {icon && hasMatchingIcon && (
        <span className={styles.iconWrapper} data-testid={`NavQuickLinkItem${icon}Icon`}>
          {/*
            TODO: Replace with actual Icon component when icons are added
            <Icon name={ICON_MAPPING[icon]} />
            
            For now, render a placeholder span. Icons needed:
            - shipping, gift, free-return, easy-contact (concierge)
            Original icons: src/assets/icons/*.svg
          */}
          <span aria-hidden="true">●</span>
        </span>
      )}

      {/* Media image */}
      {media?.url && (
        <span className={styles.imgBox} style={backgroundColor ? { backgroundColor } : undefined}>
          <Image
            src={media.url}
            alt={media.title ?? ''}
            fill
            sizes="64px"
            className={styles.img}
            aria-hidden
            data-testid="servicesImage"
          />
        </span>
      )}

      <span className={styles.label} data-testid="NavQuickLinkItemLabel">
        {urlText}
      </span>
    </button>
  );
}

NavQuickLinkItem.displayName = 'NavQuickLinkItem';
